import { useCallback } from 'react';
import { useUI } from '../context/UIContext';

// ============================================
// Types
// ============================================

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface UseToastReturn {
  showToast: (message: string, type?: ToastType) => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showInfo: (message: string) => void;
  showWarning: (message: string) => void;
  clearToast: () => void;
}

// ============================================
// Hook
// ============================================

export const useToast = (): UseToastReturn => {
  const { showFeedback, clearFeedback } = useUI();

  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    showFeedback(message, type);
  }, [showFeedback]);

  const showSuccess = useCallback((message: string) => {
    showFeedback(message, 'success');
  }, [showFeedback]);

  const showError = useCallback((message: string) => {
    showFeedback(message, 'error');
  }, [showFeedback]);

  const showInfo = useCallback((message: string) => {
    showFeedback(message, 'info');
  }, [showFeedback]);

  const showWarning = useCallback((message: string) => {
    showFeedback(message, 'warning');
  }, [showFeedback]);

  const clearToast = useCallback(() => {
    clearFeedback();
  }, [clearFeedback]);

  return {
    showToast,
    showSuccess,
    showError,
    showInfo,
    showWarning,
    clearToast,
  };
};

export default useToast;
